import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import { AlertLog } from "../components/AlertLog";
import { AlertPreferences } from "../components/AlertPreferences";
import { AlertRecipients } from "../components/AlertRecipients";
import { ConfirmDialog } from "../components/Dialog";
import { useToast } from "../components/Toast";
import { api } from "../lib/api";
import { plural } from "../lib/format";
import { useMe, useSessionChange } from "../lib/queries";
import { useTitle } from "../lib/useTitle";

export function Account() {
  useTitle("Mon compte");
  const me = useMe().data!;
  const navigate = useNavigate();
  const toast = useToast();
  const sessionChange = useSessionChange();
  const [deleting, setDeleting] = useState(false);
  const { hash } = useLocation();

  // E-mails link to /compte#alertes; the router does not scroll to anchors.
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  const leave = (message: string) => {
    sessionChange(null);
    toast(message);
    navigate("/", { replace: true });
  };
  const logout = useMutation({ mutationFn: api.logout, onSuccess: () => leave("Vous êtes déconnecté.") });
  const remove = useMutation({
    mutationFn: api.deleteAccount,
    onSuccess: () => leave("Votre compte a été supprimé."),
    onError: (error) => toast(error.message, "error"),
  });

  return (
    <div className="page page--medium">
      <div className="page-head">
        <div>
          <h1>Mon compte</h1>
          <p>{me.email}</p>
        </div>
      </div>

      {!me.approved && (
        <div className="notice notice--warning" role="status">
          Votre compte attend la validation d'un administrateur. Vos entreprises et vos alertes seront accessibles dès
          qu'il sera validé.
        </div>
      )}

      {me.approved && (
        <section className="account-block" id="alertes" aria-labelledby="alertes-title">
          <h2 id="alertes-title">Alertes par e-mail</h2>
          <p>
            Les alertes partent vers <strong>{me.email}</strong>, et vers les{" "}
            <a href="#destinataires">destinataires</a> que vous ajoutez, pour{" "}
            <Link to="/tableau-de-bord">{plural(me.watch_count, "entreprise suivie", "entreprises suivies")}</Link>.
            Un seul e-mail par jour au maximum, et seulement s'il y a du nouveau. Choisissez les événements qui
            méritent un e-mail : tous restent visibles sur les fiches des entreprises.
          </p>
          <AlertPreferences me={me} />
        </section>
      )}

      {me.approved && (
        <section className="account-block" id="destinataires" aria-labelledby="destinataires-title">
          <h2 id="destinataires-title">Destinataires des alertes</h2>
          <AlertRecipients me={me} />
        </section>
      )}

      {me.approved && (
        <section className="account-block" id="historique" aria-labelledby="historique-title">
          <h2 id="historique-title">Historique des alertes</h2>
          <p>Les e-mails d'alerte envoyés, avec leur statut de distribution. Cliquez sur un objet pour voir son contenu.</p>
          <AlertLog />
        </section>
      )}

      <section className="account-block">
        <h2>Session</h2>
        <p>Vous restez connecté 30 jours sur cet appareil.</p>
        <button type="button" className="button button--quiet" onClick={() => logout.mutate()} disabled={logout.isPending}>
          Se déconnecter
        </button>
      </section>

      <section className="account-block">
        <h2>Supprimer mon compte</h2>
        <p>
          Votre adresse et la liste de vos entreprises sont effacées immédiatement. Les alertes s'arrêtent. Cette
          action est définitive.
        </p>
        <button type="button" className="button button--quiet" onClick={() => setDeleting(true)}>
          Supprimer mon compte
        </button>
      </section>

      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        title="Supprimer votre compte ?"
        body={`${me.email} et ses ${plural(me.watch_count, "suivi", "suivis")} seront effacés. Vous ne recevrez plus aucune alerte.`}
        confirmLabel="Supprimer définitivement"
        danger
        pending={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </div>
  );
}
